/**
 * `<SettingsMemoryRoute>` — `/w/:slug/memory`
 *
 * Workspace-level long-term memory controls (conversation-memory unit,
 * PR 3.5). Promoted out of the Workspace settings page into its own
 * top-level sidebar section.
 *
 * The `memoryEnabled` switch is the kill switch that gates the entire
 * long-term memory feature (recall + automatic extraction). When off,
 * no embeddings are generated and nothing is written.
 */

import { useState, type ReactNode } from "react";

import { useWorkspace } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Textarea,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Database, Sparkles, X, type LucideIcon } from "../components/icons";

import { PageShell } from "../components/page-shell";
import { ApiError } from "../lib/api/errors";
import { useContactsClient } from "../lib/api/use-contacts-client";
import type { MemorySettings, MemorySettingsPatch } from "../lib/api/types";
import {
  useArchiveVariable,
  useCreateVariable,
  useUnarchiveVariable,
  useVariableCatalog,
  useVariableUsage,
  type CreateVariableInput,
  type VariableDto,
} from "../agents/api/variables";

const INPUT_CLASS = "h-9 rounded-lg text-[13px] placeholder:text-[color:var(--color-fg-subtle)]";

function SettingsCard({
  icon: Icon,
  title,
  action,
  children,
}: {
  icon: LucideIcon;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-semibold tracking-[0.01em] text-foreground">{title}</h3>
          </div>
          {action}
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </section>
  );
}

function FieldRow({ label, hint, children }: { label: string; hint: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-foreground">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}

export function SettingsMemoryRoute() {
  const client = useContactsClient();
  const qc = useQueryClient();
  const { workspace } = useWorkspace();
  const queryKey = ["workspace", "memory-settings"] as const;

  // Agents blocking the master switch from being turned off (each still
  // references a #memory mention in its published prompt). Drives the dialog.
  const [blockedBy, setBlockedBy] = useState<{ id: string; name: string }[] | null>(null);

  const query = useQuery({
    queryKey,
    queryFn: () => client.getMemorySettings(),
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (patch: MemorySettingsPatch) => client.updateMemorySettings(patch),
    onSuccess: (settings) => {
      qc.setQueryData<MemorySettings>(queryKey, settings);
      toast.success("Memory settings saved");
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "MEMORY_IN_USE") {
        const agents = (error.details?.["agents"] as { id: string; name: string }[]) ?? [];
        setBlockedBy(agents);
        return;
      }
      toast.error("Could not save memory settings");
      void qc.invalidateQueries({ queryKey });
    },
  });

  return (
    <PageShell
      className="mx-auto w-full max-w-6xl space-y-5 p-6"
      data-testid="settings-memory"
      description="When enabled, the assistant remembers durable facts about each contact across conversations and recalls them in future runs."
    >
      <div data-testid="memory-settings-card">
        <SettingsCard icon={Sparkles} title="Long-term memory">
          {query.isPending ? (
            <Skeleton className="h-10 w-full" />
          ) : query.error || !query.data ? (
            <p className="text-[13px] text-destructive" data-testid="memory-settings-error">
              Could not load memory settings.
            </p>
          ) : (
            <div className="space-y-5">
              <FieldRow
                label="Enable long-term memory"
                hint="Master switch for the whole workspace. When off, no agent remembers or recalls anything — every conversation starts fresh."
              >
                <Switch
                  id="memory-enabled"
                  data-testid="memory-enabled-switch"
                  checked={query.data.memoryEnabled}
                  disabled={mutation.isPending}
                  onCheckedChange={(checked) => mutation.mutate({ memoryEnabled: checked })}
                />
              </FieldRow>

              {!query.data.memoryEnabled && (
                <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
                  Memory is off. Turn it on to configure how memories are recalled and kept.
                </p>
              )}

              <div className="-mx-5 border-t border-dashed border-border" />

              <div
                className={`space-y-5 transition-opacity ${
                  query.data.memoryEnabled ? "" : "pointer-events-none opacity-50"
                }`}
              >
                <FieldRow
                  label="Memories recalled per run"
                  hint="Top-K memories fetched and injected when a conversation starts."
                >
                  <Input
                    id="memory-top-k"
                    data-testid="memory-top-k-input"
                    type="number"
                    min={1}
                    max={50}
                    defaultValue={query.data.memoryRecallTopK}
                    disabled={mutation.isPending || !query.data.memoryEnabled}
                    className={`${INPUT_CLASS} w-32 shrink-0`}
                    onBlur={(e) => {
                      const next = Number(e.currentTarget.value);
                      if (
                        Number.isInteger(next) &&
                        next >= 1 &&
                        next <= 50 &&
                        next !== query.data.memoryRecallTopK
                      ) {
                        mutation.mutate({ memoryRecallTopK: next });
                      }
                    }}
                  />
                </FieldRow>

                <FieldRow
                  label="Retention (days)"
                  hint="Leave blank to keep memories indefinitely."
                >
                  <Input
                    id="memory-retention"
                    data-testid="memory-retention-input"
                    type="number"
                    min={1}
                    max={3650}
                    placeholder="Forever"
                    defaultValue={query.data.memoryRetentionDays ?? ""}
                    disabled={mutation.isPending || !query.data.memoryEnabled}
                    className={`${INPUT_CLASS} w-32 shrink-0`}
                    onBlur={(e) => {
                      const raw = e.currentTarget.value.trim();
                      const next = raw === "" ? null : Number(raw);
                      if (next !== null && !(Number.isInteger(next) && next >= 1 && next <= 3650)) {
                        return;
                      }
                      if (next !== query.data.memoryRetentionDays) {
                        mutation.mutate({ memoryRetentionDays: next });
                      }
                    }}
                  />
                </FieldRow>
              </div>
            </div>
          )}
        </SettingsCard>
      </div>

      <VariableCatalogCard slug={workspace.slug} />

      <AlertDialog
        open={blockedBy !== null}
        onOpenChange={(open) => {
          if (!open) setBlockedBy(null);
        }}
      >
        <AlertDialogContent data-testid="memory-in-use-block">
          <AlertDialogHeader>
            <AlertDialogTitle>Memory is still in use</AlertDialogTitle>
            <AlertDialogDescription>
              You can only turn off workspace memory when no agent uses #memory in its prompt.
              Remove the #memory mentions from{" "}
              {blockedBy?.length === 1 ? "this agent" : "these agents"} and republish, then try
              again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {blockedBy && blockedBy.length > 0 ? (
            <ul
              className="list-disc pl-5 text-[13px] text-muted-foreground"
              data-testid="memory-in-use-agents"
            >
              {blockedBy.map((a) => (
                <li key={a.id}>{a.name}</li>
              ))}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setBlockedBy(null)}>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function VarRefChip({ slug }: { slug: string }) {
  return (
    <span className="shrink-0 rounded-[5px] bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
      #variable/{slug}
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      {type}
    </span>
  );
}

function SensitiveChip() {
  const color = "var(--color-warning)";
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs"
      style={{ color, background: `color-mix(in oklch, ${color} 12%, transparent)` }}
    >
      sensitive
    </span>
  );
}

function VariableCatalogCard({ slug }: { slug: string }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Set when archiving a variable that agents still use — drives the warning.
  const [confirmTarget, setConfirmTarget] = useState<VariableDto | null>(null);
  const catalogQuery = useVariableCatalog(slug);
  const archivedQuery = useVariableCatalog(slug, "archived", showArchived);
  const usageQuery = useVariableUsage(slug, confirmTarget?.id, Boolean(confirmTarget));
  const createVariable = useCreateVariable(slug);
  const archiveVariable = useArchiveVariable(slug);
  const unarchiveVariable = useUnarchiveVariable(slug);

  function doArchive(id: string) {
    archiveVariable.mutate(id, {
      onSuccess: () => {
        toast.success("Variable archived");
        setConfirmTarget(null);
      },
      onError: () => toast.error("Could not archive variable"),
    });
  }

  /** Archive directly when no agent uses it; otherwise warn first. */
  function requestArchive(v: VariableDto) {
    if (v.agent_count && v.agent_count > 0) setConfirmTarget(v);
    else doArchive(v.id);
  }

  const [form, setForm] = useState<CreateVariableInput>({
    key: "",
    display_name: "",
    value_type: "text",
    is_sensitive: false,
    description: undefined,
  });

  function resetForm() {
    setForm({
      key: "",
      display_name: "",
      value_type: "text",
      is_sensitive: false,
      description: undefined,
    });
  }

  function handleCreate() {
    createVariable.mutate(form, {
      onSuccess: () => {
        toast.success("Variable created");
        setDialogOpen(false);
        resetForm();
      },
      onError: () => {
        toast.error("Could not create variable");
      },
    });
  }

  const variables = catalogQuery.data ?? [];
  const archived = archivedQuery.data ?? [];

  return (
    <>
      <div data-testid="variable-catalog-card">
        <SettingsCard
          icon={Database}
          title="Variable catalog"
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                className="h-8 rounded-md text-[13px] hover:bg-secondary-hover"
                data-testid="toggle-archived"
                onClick={() => setShowArchived((s) => !s)}
              >
                {showArchived ? "Hide archived" : "Show archived"}
              </Button>
              <Button
                size="sm"
                className="h-8 rounded-md text-[13px]"
                onClick={() => setDialogOpen(true)}
              >
                Add variable
              </Button>
            </div>
          }
        >
          <p className="-mt-1 mb-4 text-xs text-muted-foreground">
            Structured data agents can write per contact.
          </p>

          {catalogQuery.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : catalogQuery.error ? (
            <p className="text-[13px] text-destructive">Could not load variable catalog.</p>
          ) : variables.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-muted">
                <Database className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-sm font-medium text-foreground">No variables yet</p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                Add a variable to allow agents to write structured data per contact.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {variables.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-[13px]"
                >
                  <VarRefChip slug={v.key} />
                  <span className="min-w-0 flex-1 truncate text-foreground">{v.display_name}</span>
                  <TypeBadge type={v.value_type} />
                  {v.is_sensitive ? <SensitiveChip /> : null}
                  {v.agent_count ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {v.agent_count} {v.agent_count === 1 ? "agent" : "agents"}
                    </span>
                  ) : null}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 rounded-md text-[13px]"
                    disabled={archiveVariable.isPending}
                    onClick={() => requestArchive(v)}
                  >
                    Archive
                  </Button>
                </div>
              ))}
            </div>
          )}

          {showArchived && (
            <div className="mt-4 space-y-2" data-testid="archived-variables">
              <p className="text-xs font-medium text-muted-foreground">Archived</p>
              {archivedQuery.isPending ? (
                <Skeleton className="h-9 w-full" />
              ) : archivedQuery.error ? (
                <p className="text-[13px] text-destructive">Could not load archived variables.</p>
              ) : archived.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">No archived variables.</p>
              ) : (
                <div className="space-y-2">
                  {archived.map((v) => (
                    <div
                      key={v.id}
                      className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 text-[13px] text-muted-foreground"
                    >
                      <VarRefChip slug={v.key} />
                      <span className="min-w-0 flex-1 truncate">{v.display_name}</span>
                      <TypeBadge type={v.value_type} />
                      {v.is_sensitive ? <SensitiveChip /> : null}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 shrink-0 rounded-md text-[13px]"
                        disabled={unarchiveVariable.isPending}
                        onClick={() =>
                          unarchiveVariable.mutate(v.id, {
                            onSuccess: () => toast.success("Variable restored"),
                            onError: () => toast.error("Could not restore variable"),
                          })
                        }
                      >
                        Unarchive
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </SettingsCard>
      </div>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-lg gap-0 rounded-3xl p-0 sm:rounded-3xl">
          <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
            <DialogTitle className="text-base font-medium tracking-[0.01em]">
              Add variable
            </DialogTitle>
            <DialogDescription className="text-[13px] text-muted-foreground">
              Structured data agents can write per contact.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-5 pb-2">
            <div className="space-y-1.5">
              <Label htmlFor="var-key" className="text-[13px] font-medium text-foreground">
                Key
              </Label>
              <Input
                id="var-key"
                placeholder="e.g. shoe_size"
                className={INPUT_CLASS}
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Lowercase snake_case, e.g. shoe_size</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="var-display-name" className="text-[13px] font-medium text-foreground">
                Display Name
              </Label>
              <Input
                id="var-display-name"
                placeholder="Shoe Size"
                className={INPUT_CLASS}
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="var-type" className="text-[13px] font-medium text-foreground">
                Type
              </Label>
              <Select
                value={form.value_type}
                onValueChange={(v) =>
                  setForm({ ...form, value_type: v as CreateVariableInput["value_type"] })
                }
              >
                <SelectTrigger id="var-type" className="h-9 rounded-lg text-[13px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="var-description" className="text-[13px] font-medium text-foreground">
                Description (optional)
              </Label>
              <Textarea
                id="var-description"
                placeholder="What this variable tracks"
                className="min-h-0 resize-none rounded-lg text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
                value={form.description ?? ""}
                onChange={(e) => setForm({ ...form, description: e.target.value || undefined })}
              />
            </div>

            <div className="rounded-lg border border-border">
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="var-sensitive"
                    className="text-[13px] font-medium text-foreground"
                  >
                    Sensitive
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Redacts the value in logs and traces (treated as PII).
                  </p>
                </div>
                <Switch
                  id="var-sensitive"
                  checked={form.is_sensitive}
                  onCheckedChange={(checked) => setForm({ ...form, is_sensitive: checked })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex-row gap-2 px-5 pb-5 pt-4">
            <DialogClose asChild>
              <Button
                variant="secondary"
                size="sm"
                className="h-9 flex-1 gap-1.5 rounded-md text-[13px] hover:bg-secondary-hover"
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </DialogClose>
            <Button
              size="sm"
              className="h-9 flex-1 gap-1.5 rounded-md text-[13px]"
              disabled={!form.key || !form.display_name || createVariable.isPending}
              onClick={handleCreate}
            >
              <Check className="h-4 w-4" />
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmTarget(null);
        }}
      >
        <AlertDialogContent data-testid="archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive “{confirmTarget?.display_name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This variable is used by {confirmTarget?.agent_count}{" "}
              {confirmTarget?.agent_count === 1 ? "agent" : "agents"}. Archiving disables it for
              them until you restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {usageQuery.data && usageQuery.data.agents.length > 0 ? (
            <ul
              className="list-disc pl-5 text-[13px] text-muted-foreground"
              data-testid="archive-confirm-agents"
            >
              {usageQuery.data.agents.map((a) => (
                <li key={a.id}>
                  {a.name} <span className="opacity-70">({a.mode})</span>
                </li>
              ))}
            </ul>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveVariable.isPending}
              onClick={() => confirmTarget && doArchive(confirmTarget.id)}
            >
              Archive anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
